import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const ItemContainer = styled.li`
  list-style: none;
  margin: 2px 6px;
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: var(--nx-r-sm);
  cursor: pointer;
  color: ${(p) => (p.$active ? "var(--nx-primary-700)" : "var(--nx-text)")};
  background: ${(p) => (p.$active ? "var(--nx-primary-50)" : "transparent")};
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  transition:
    background 0.2s ease,
    color 0.2s ease;
  gap: 12px;
  position: relative;

  &:hover {
    background: var(--nx-primary-50);
    color: var(--nx-primary-700);
  }

  &:focus-visible {
    outline: 2px solid var(--nx-primary);
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${(p) => (p.$active ? "var(--nx-primary)" : "inherit")};

  svg {
    color: inherit;
  }
`;

const Label = styled.span`
  font-size: var(--nx-fs-body);
  font-weight: inherit;
  white-space: nowrap;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  transition: opacity 0.2s ease;
`;

const Caret = styled.span`
  margin-left: auto;
  font-size: 10px;
  color: var(--nx-muted);
`;

/* Cadeado dos itens `locked` (capacidade fora do plano — ADR-0026). O item
   continua clicável e navegável de propósito: a rota renderiza o convite de
   upgrade. Bloquear, não esconder. */
const LockBadge = styled.span`
  margin-left: auto;
  font-size: 11px;
  line-height: 1;
  opacity: 0.75;
`;

const SubMenu = styled.ul`
  list-style: none;
  padding-left: 32px;
  margin-top: 4px;
  display: ${(p) => (p.$open ? "block" : "none")};
`;

/* Rótulo de grupo (não navegável) — maiúsculas pequenas, neutro. */
const SectionLabel = styled.li`
  list-style: none;
  margin: 14px 12px 4px;
  font-size: var(--nx-fs-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nx-muted);
  white-space: nowrap;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
`;

/* Card "Minha empresa" — destaque no topo do menu. */
const CompanyCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: var(--nx-r-md);
  cursor: pointer;
  background: var(--nx-primary-50);
  border: 1px solid var(--nx-line);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: var(--nx-sh-sm);
  }

  &:focus-visible {
    outline: 2px solid var(--nx-primary);
    outline-offset: 2px;
  }
`;

const CompanyAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--nx-pill);
  background: var(--nx-primary);
  color: var(--nx-surface);
  font-size: var(--nx-fs-body);
  font-weight: 700;
  text-transform: uppercase;
`;

const CompanyText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
`;

const CompanyName = styled.span`
  font-size: var(--nx-fs-body);
  font-weight: 700;
  color: var(--nx-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanySubtitle = styled.span`
  font-size: var(--nx-fs-xs);
  color: var(--nx-muted);
`;

const SidebarItem = ({
  item,
  collapsed,
  openKeys,
  onToggleOpen,
  activeKey,
  level = 0,
  /** Callback chamado após navegar ou acionar onClick — usado para fechar drawer no mobile */
  onItemClick,
}) => {
  const navigate = useNavigate();

  // Rótulo de seção — não clicável, não navegável.
  if (item.type === "section") {
    return (
      <SectionLabel $collapsed={collapsed} aria-hidden={collapsed || undefined}>
        {item.label}
      </SectionLabel>
    );
  }

  const isOpen = openKeys.includes(item.key);
  const isActive = activeKey === item.key;

  // ✅ A Lógica Mestra do Clique
  const handleClick = (e) => {
    e.stopPropagation(); // Evita que o clique "suba" para elementos pai

    // 1. Prioridade: Se o item tem uma função onClick (WhatsApp, card empresa)
    if (item.onClick) {
      item.onClick();
      onItemClick?.();
      return;
    }

    // 2. Se tem filhos, alterna o abrir/fechar (não fecha drawer)
    if (item.children && item.children.length > 0) {
      onToggleOpen(item.key);
      return;
    }

    // 3. Se tem link, navega e fecha o drawer
    if (item.link) {
      navigate(item.link);
      onItemClick?.();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick(e);
    }
  };

  // Card destacado "Minha empresa".
  if (item.key === "empresa") {
    const name = item.label || "Minha empresa";
    const initial = name.trim().charAt(0) || "M";
    return (
      <ItemContainer>
        <CompanyCard
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={onKeyDown}
          aria-label={`${name} — minha empresa`}
          title={collapsed ? name : ""}
        >
          <CompanyAvatar aria-hidden="true">{initial}</CompanyAvatar>
          {!collapsed && (
            <CompanyText $collapsed={collapsed}>
              <CompanyName>{name}</CompanyName>
              <CompanySubtitle>Minha empresa</CompanySubtitle>
            </CompanyText>
          )}
        </CompanyCard>
      </ItemContainer>
    );
  }

  // Item de link/navegação direta ativo recebe aria-current="page".
  const isNavTarget = Boolean(item.link || item.onClick);

  return (
    <ItemContainer>
      <ItemContent
        $active={isActive}
        $collapsed={collapsed}
        onClick={handleClick}
        title={collapsed ? item.label : ""}
        role="button"
        tabIndex={0}
        aria-current={isActive && isNavTarget ? "page" : undefined}
        aria-label={
          item.locked
            ? `${item.label} — fora do seu plano`
            : collapsed
              ? item.label
              : undefined
        }
        onKeyDown={onKeyDown}
      >
        <IconWrapper $active={isActive}>{item.icon}</IconWrapper>
        {!collapsed && <Label $collapsed={collapsed}>{item.label}</Label>}

        {/* Cadeado: capacidade fora do plano. Vem ANTES do caret para que um
            grupo bloqueado mostre os dois (cadeado + seta de expandir). */}
        {!collapsed && item.locked && (
          <LockBadge title="Fora do seu plano">🔒</LockBadge>
        )}

        {/* Indicador de submenu (opcional) */}
        {!collapsed && item.children && (
          <Caret aria-hidden="true">{isOpen ? "▼" : "▶"}</Caret>
        )}
      </ItemContent>

      {/* Renderização Recursiva de Filhos */}
      {item.children && !collapsed && (
        <SubMenu $open={isOpen}>
          {item.children.map((child) => (
            <SidebarItem
              key={child.key}
              item={child}
              collapsed={collapsed}
              openKeys={openKeys}
              onToggleOpen={onToggleOpen}
              activeKey={activeKey}
              level={level + 1}
              onItemClick={onItemClick}
            />
          ))}
        </SubMenu>
      )}
    </ItemContainer>
  );
};

SidebarItem.propTypes = {
  item: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  openKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleOpen: PropTypes.func.isRequired,
  activeKey: PropTypes.string,
  level: PropTypes.number,
  onItemClick: PropTypes.func,
};

export default SidebarItem;
